/**
 * The catalogue of what People Space *could* report on, so the board can say what went unused.
 *
 * ── Why a hand-maintained list ───────────────────────────────────────────────────────────────
 * "Unused" is only meaningful against a denominator. The API returns the events it SAW; the events
 * it did not see cannot be derived from that alone. This file is the other half.
 *
 * ── The distinction that makes this safe ─────────────────────────────────────────────────────
 * Most views in this app emit no events at all. A naive "unused features" list would therefore
 * flag them as dead when they are merely unmeasured — and someone could retire a well-used feature
 * on that evidence. So every entry declares whether it is `instrumented`. That produces two
 * different recommendations, and conflating them is the whole risk:
 *
 *   instrumented + zero events  → nobody used it. A real candidate for retirement.
 *   not instrumented            → we have no idea. The recommendation is to MEASURE, not to cut.
 *
 * When you add a feature, add it here. When you add tracking to an existing feature, flip
 * `instrumented` and list its events. A stale catalogue degrades honestly — a missing entry just
 * means the board says nothing about that feature, rather than saying something false.
 */

export interface CatalogEntry {
  /** What a reader calls it, not the component name. */
  feature: string;
  /** Sidebar area, so recommendations can be read by team. */
  area: string;
  /** Event names this feature emits. Empty when it emits none. */
  events: string[];
  /**
   * False when the feature has no instrumentation of its own. Such a feature still produces
   * `page_viewed`, so traffic is visible — what people DO there is not.
   */
  instrumented: boolean;
  /** Why it is not instrumented, or what an event would need to capture. Shown as the next step. */
  note?: string;
}

/**
 * Board-action events whose NAME is produced at runtime by
 * `taActionBoardApi.boardActionEvent(action.type)` rather than written as a literal.
 *
 * Exported so `featureCatalog.test.ts` can tell "derived at runtime" apart from "nothing emits
 * this". That distinction is not academic: nine catalogued names once matched no emitter at all,
 * and the board reported three actively-used tabs as unused.
 */
export const RUNTIME_DERIVED_EVENTS = ['ta_board_archive', 'ta_board_move'];

export const FEATURE_CATALOG: CatalogEntry[] = [
  {
    feature: 'Outreach (LinkedIn InMails)',
    area: 'Talent Acquisition',
    events: [
      'ta_outreach_imported',
      'ta_outreach_brief_compiled',
      'ta_outreach_brief_edited',
      'ta_outreach_drafted',
      'ta_outreach_draft_refined',
      'ta_outreach_refine_undone',
      'ta_outreach_composer_opened',
      'ta_outreach_marked_sent',
      'ta_outreach_follow_up_closed',
      'ta_outreach_dropped',
      'ta_outreach_send_run_finished',
      'ta_outreach_profile_captured',
      'ta_outreach_shortlist_decided',
      'ta_outreach_sourcing_job_created',
      'ta_outreach_shortlist_bulk_accepted',
      'ta_outreach_writing_rules_saved',
      'ta_outreach_sample_draft_written',
      'ta_outreach_sourcing_run_finished',
      'ta_outreach_quality_check_downloaded',
    ],
    instrumented: true,
  },
  {
    feature: 'TA Action Boards — navigation',
    area: 'Talent Acquisition',
    events: ['ta_board_tab_opened'],
    instrumented: true,
  },
  // The board's tabs are catalogued SEPARATELY, because "the board is used" is not an actionable
  // fact — every recruiter opens it. Which of its five tabs earns its place is the real question,
  // and only a per-tab denominator can answer it. Action names are derived from the action type in
  // `taActionBoardApi.boardActionEvent`, so a new action is tracked without a second edit.
  {
    feature: 'TA board — Feedback tab (archive, move)',
    area: 'Talent Acquisition',
    events: ['ta_board_archive', 'ta_board_move', 'ta_stage_changed'],
    instrumented: true,
  },
  {
    feature: 'TA board — Scheduling tab (holds, ask, offers)',
    area: 'Talent Acquisition',
    events: ['ta_board_holds', 'ta_board_ask', 'ta_board_withdraw', 'ta_board_panel_saved'],
    instrumented: true,
  },
  {
    feature: 'TA board — Final rounds tab',
    area: 'Talent Acquisition',
    events: ['ta_board_fr_book', 'ta_board_fr_config'],
    instrumented: true,
  },
  {
    feature: 'TA board — SLA tab (chasing feedback)',
    area: 'Talent Acquisition',
    events: ['ta_board_feedback_nudged'],
    instrumented: true,
  },
  {
    feature: 'Candidate requests',
    area: 'Talent Acquisition',
    events: [
      'ta_requests_loaded',
      // Aug 2026: the tab sends the reviewed reply instead of saving a Gmail draft, so the
      // two draft events no longer exist. Renamed here, not dropped — the feature is still
      // instrumented, and usage charts move to the send names.
      'ta_requests_reply_sent',
      'ta_requests_reply_send_failed',
      // The server refused a second reply to the same message. Rare by design; worth counting,
      // because a rising number means recruiters are being invited to double-send somewhere.
      'ta_requests_reply_duplicate',
      'ta_requests_marked_read',
      // Thumbs on a draft. The event carries the vote/category/source only — the recruiter's comment
      // is candidate-adjacent text and never reaches an analytics sink.
      'ta_requests_draft_rated',
      // FINN-side interview cancel (Aug 2026): performed, refused-as-duplicate, or failed.
      'ta_requests_interview_cancelled',
      'ta_requests_interview_cancel_duplicate',
      'ta_requests_interview_cancel_failed',
      // 'ta_requests_replies_exported' removed Aug 13 2026 with the export panel itself (Sofia's
      // call — the corpus it fed is collected, and further exports measured as not worth taking).
      // Its Amplitude history stays valid for the era it covers; nothing emits it now.
      'ta_requests_gmail_expired',
    ],
    instrumented: true,
  },
  {
    feature: 'Reference Letters',
    area: 'Offboarding',
    events: [
      'reference_letters_viewed',
      'reference_letters_search_submitted',
      'reference_letters_search_completed',
      'reference_letters_leaver_selected',
      'reference_letters_supervisor_reminded',
      'reference_letter_request_viewed',
      'reference_letter_request_submitted',
      'reference_letters_draft_opened',
      'reference_letters_draft_downloaded',
    ],
    instrumented: true,
  },
  {
    feature: 'Chat with Boris',
    area: 'Boris, the Business Partner',
    events: ['boris_question_asked', 'boris_answer_received', 'boris_answer_rated', 'boris_request_opened'],
    instrumented: true,
  },
  {
    feature: 'Equity Calculator',
    area: 'People Operations',
    events: ['equity_calculator_viewed'],
    instrumented: true,
  },
  {
    feature: 'Absence Insights',
    area: 'People Operations',
    events: ['absence_insights_refresh_clicked'],
    instrumented: true,
  },
  {
    feature: 'Usage Analytics',
    area: 'People Operations',
    // `usage_analytics_viewed` matters here specifically: the other two only fire on interaction,
    // so a board that is read and not clicked would report ITSELF as unused.
    events: ['usage_analytics_viewed', 'usage_analytics_refreshed', 'usage_analytics_range_changed'],
    instrumented: true,
  },
  {
    feature: 'Interview Links',
    area: 'Talent Acquisition',
    events: [
      'interview_link_created',
      'interview_link_updated',
      'interview_link_closed',
      'interview_link_reopened',
    ],
    instrumented: true,
    note: 'Creation is the top of the booking funnel — pair with booking_confirmed in Amplitude for link-to-booking conversion.',
  },
  {
    feature: 'Log (interviews, decisions, emails)',
    area: 'Talent Acquisition',
    events: ['log_topic_changed', 'log_filter_changed', 'log_searched'],
    instrumented: true,
    note: 'log_topic_changed answers whether the newer topics (decisions, emails, screening) earn their place beside interviews.',
  },
  {
    feature: 'TA Cockpit — navigation',
    area: 'Talent Acquisition',
    events: ['ta_cockpit_tab_changed', 'ta_cockpit_subtab_changed'],
    instrumented: true,
    note: 'Sep 2026: the Cockpit emitted nothing until this. Its sub-tabs are also in the path, so which half of Tracking or Role Prioritisation people work in is visible on the usage tree without an event at all.',
  },
  {
    feature: 'TA Cockpit — Planning (roster, target)',
    area: 'Talent Acquisition',
    events: ['ta_cockpit_team_saved', 'ta_cockpit_target_committed'],
    instrumented: true,
  },
  {
    feature: 'TA Cockpit — Pipeline Radar',
    area: 'Talent Acquisition',
    events: ['ta_radar_filter_changed', 'ta_radar_role_opened'],
    instrumented: true,
  },
  // ── Not instrumented. These are "go and measure", never "retire". ──────────────────────────
  {
    feature: 'TA Cockpit — Role Prioritisation model',
    area: 'Talent Acquisition',
    events: [],
    instrumented: false,
    note: 'The tab is counted, the work inside it is not: editing the effort model, moving hours between clusters and marking someone out of office all emit nothing. Those are the decisions the tab exists for.',
  },
  {
    feature: 'TA Cockpit — Tracking figures',
    area: 'Talent Acquisition',
    events: [],
    instrumented: false,
    note: 'Delivery and Hiring are visible as sub-tabs now, but expanding a bucket, opening a role or re-reading the warehouse emit nothing.',
  },
  {
    feature: 'Manager Review',
    area: 'FINNies',
    events: [],
    instrumented: false,
    note: 'No events. Completion rate is the obvious first metric.',
  },
  {
    feature: 'Onboarding Scheduling',
    area: 'Onboarding',
    events: [],
    instrumented: false,
    note: 'No events.',
  },
  {
    feature: 'JobAuto Rechner',
    area: 'People Operations',
    events: [],
    instrumented: false,
    note: 'No events.',
  },
  {
    feature: 'Candidate booking page',
    area: 'Talent Acquisition',
    events: [],
    instrumented: false,
    note: 'Fully instrumented, but candidates are signed out so its EVENTS cannot reach this sink. Booking outcomes are not missing — see Interview outcomes above, built from the booking records. Only the drop-off (opened, never booked) is Amplitude-only, because a non-booking leaves no record.',
  },
];

export type Recommendation =
  | { kind: 'unused'; entry: CatalogEntry }
  | { kind: 'unmeasured'; entry: CatalogEntry };

/**
 * Split the catalogue against what actually fired.
 *
 * A feature counts as used if ANY of its events appeared — one signal is enough to prove life, and
 * requiring all of them would flag a feature as dead over one unused button.
 */
export function buildRecommendations(observedEvents: string[]): Recommendation[] {
  const seen = new Set(observedEvents);
  const recommendations: Recommendation[] = [];
  for (const entry of FEATURE_CATALOG) {
    if (!entry.instrumented) {
      recommendations.push({ kind: 'unmeasured', entry });
      continue;
    }
    if (!entry.events.some((event) => seen.has(event))) {
      recommendations.push({ kind: 'unused', entry });
    }
  }
  return recommendations;
}
